import { Component, OnInit } from '@angular/core';

import { DynamicHtmlDirective } from '@frontend/vanilla/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { RgLimitInfoData } from '../../../../model/depositInfo.model';
import { BehaviorDataService } from '../../../../service/behaviorData.service';
import { DataService } from '../../../../service/data.service';

@Component({
    selector: 'bqd-threshold-limit',
    templateUrl: 'threshold-limit-messages.component.html',
    styleUrls: ['threshold-limit-messages.component.scss'],
    imports: [TranslateModule, DynamicHtmlDirective],
})
export class ThresholdLimitMessagesComponent implements OnInit {
    inputData: RgLimitInfoData;
    enteredAmount: any;
    frequency: string;
    showMessage = false;
    isBucketChanged = true;

    limitBuckets: any = {
        daily: { one: 0, two: 0, three: 0 },
        weekly: { one: 0, two: 0, three: 0 },
        monthly: { one: 0, two: 0, three: 0 },
    };

    bucketStatus: any = {
        daily: { one: false, two: false, three: false, threshold: false, limitMet: false },
        weekly: { one: false, two: false, three: false, threshold: false, limitMet: false },
        monthly: { one: false, two: false, three: false, threshold: false, limitMet: false },
    };

    constructor(
        private behaviourData: BehaviorDataService,
        private translateService: TranslateService,
        public dataService: DataService,
    ) {}

    ngOnInit() {
        this.getRgLimitInfo();
        this.behaviourData.getEnteredAmount.subscribe((message) => {
            if (message) {
                this.resetState();
                this.enteredAmount = message;
                this.getCurrentBucket();
            }
        });
    }

    getRgLimitInfo() {
        if (this.dataService.RgLimitData) {
            this.inputData = this.dataService.RgLimitData;
            this.setBucketValues();
        }
    }

    getCurrentBucket() {
        this.inputData?.rglimitInfo?.forEach((item) => {
            this.setBucketForType(item);
        });
        this.toggleLimitMsg();
    }

    setBucketForType(item: any) {
        const { type, actualPurchase, rgLimit } = item;
        const buckets = this.getBuckets(type);
        const currentBucket = this.getCurrentBucketForType(actualPurchase.value, buckets, rgLimit.value);

        if (currentBucket) {
            this.bucketStatus[type][currentBucket] = true;
        }
    }

    getBuckets(type: string) {
        return this.limitBuckets[type.toLowerCase()] || {};
    }

    getCurrentBucketForType(value: number, buckets: any, limit: number) {
        if (value >= buckets.one && value < buckets.two) return 'one';
        if (value >= buckets.two && value < buckets.three) return 'two';
        if (value >= buckets.three && value < limit) return 'three';
        return null;
    }

    setBucketValues() {
        this.inputData?.rglimitInfo?.forEach((item) => {
            const { days, rgLimit } = item;
            let limitBuckets = this.getLimitBucketsForPeriod(days);

            this.calculateBucketValuesAndAssign(limitBuckets, rgLimit.value);
        });
    }

    getLimitBucketsForPeriod(days: number) {
        if (days === 1) return this.limitBuckets.daily;
        if (days <= 7) return this.limitBuckets.weekly;
        return this.limitBuckets.monthly;
    }

    calculateBucketValuesAndAssign(buckets: any, rgLimitValue: number) {
        buckets.one = this.calculateBucketValues(this.inputData.rg_first_bucket_threshold, rgLimitValue);
        buckets.two = this.calculateBucketValues(this.inputData.rg_second_bucket_threshold, rgLimitValue);
        buckets.three = this.calculateBucketValues(this.inputData.rg_third_bucket_threshold, rgLimitValue);
    }

    calculateBucketValues(bucketValue: any, rglimit: any) {
        return (bucketValue * rglimit) / 100;
    }

    toggleLimitMsg() {
        const amount = Number(this.enteredAmount);
        if (this.inputData) {
            this.inputData.rglimitInfo?.forEach((item) => {
                if (!item.requestPending) {
                    this.checkBucketLimitMsg(amount, item);
                }
            });
        }
        this.checkBucketChanged();
        this.setFrequency(); // Call setFrequency when the message is toggled
    }

    checkBucketLimitMsg(amount: number, item: any) {
        const { type, actualPurchase, rgLimit } = item;
        const calculatedAmount = amount + actualPurchase.value;
        const buckets = this.getBuckets(type);
        const currentBucket = this.getCurrentBucketForType(calculatedAmount, buckets, rgLimit.value);

        if (currentBucket) {
            this.showMessage = true;
            this.bucketStatus[type].limitMet = currentBucket === 'three' && calculatedAmount === rgLimit.value;
            this.bucketStatus[type].threshold = currentBucket !== 'three';
            this.bucketStatus[type][currentBucket] = true;
        } else {
            this.showMessage = false;
            this.bucketStatus[type].threshold = true;
        }
    }

    checkBucketChanged() {
        if (
            Object.values(this.bucketStatus.daily).includes(true) ||
            Object.values(this.bucketStatus.weekly).includes(true) ||
            Object.values(this.bucketStatus.monthly).includes(true)
        ) {
            this.showMessage = false;
            this.isBucketChanged = false;
        }
    }

    setFrequency() {
        if (this.isThresholdLimitMet('monthly')) this.frequency = 'Monthly';
        else if (this.isThresholdLimitMet('weekly')) this.frequency = 'Weekly';
        else if (this.isThresholdLimitMet('daily')) this.frequency = 'Daily';
        else this.frequency = 'Daily'; // Default to Daily
    }

    isThresholdLimitMet(type: string): boolean {
        return Object.values(this.bucketStatus[type]).includes(true) && !this.bucketStatus[type].limitMet;
    }

    translateFrequency() {
        return this.translateService.instant('PARTY.CASHIER.web.deposit.threshold.limit.' + this.frequency?.toLowerCase());
    }

    hasThreshold = (type: string) => this.bucketStatus[type]['threshold'];
    hasLimitMet = (type: string) => this.bucketStatus[type]['limitMet'];

    get threshold() {
        return this.isBucketChanged && ['daily', 'weekly', 'monthly'].some((type) => this.hasThreshold(type) && !this.hasLimitMet(type));
    }

    get thresholdAndLimitMet() {
        return this.isBucketChanged && ['daily', 'weekly', 'monthly'].some((type) => this.hasThreshold(type) && this.hasLimitMet(type));
    }

    resetState() {
        this.showMessage = false;
        this.frequency = '';
        this.isBucketChanged = true;
        this.resetBucketStatus('daily');
        this.resetBucketStatus('weekly');
        this.resetBucketStatus('monthly');
    }

    resetBucketStatus(type: string) {
        Object.keys(this.bucketStatus[type]).forEach((key) => {
            this.bucketStatus[type][key] = false;
        });
    }
}
